// Supabase's REST gateway rejects requests whose URL is too long with HTTP 400.
// A `.in("col", ids)` filter serializes every id into the query string, so a
// company with a large catalog (hundreds of variantes) overflows the limit and
// the whole fetch fails. Splitting the ids into batches keeps every request
// URL well under the gateway limit regardless of catalog size.
//
// 100 ids ≈ 100 * (36-char UUID + 3-char encoded comma) ≈ 3.9 KB, comfortably
// below the ~8 KB threshold where 400s start appearing.
export const SUPABASE_IN_CHUNK_SIZE = 100;

export function chunkArray<T>(
  items: T[],
  size: number = SUPABASE_IN_CHUNK_SIZE,
): T[][] {
  if (size <= 0) return [items];
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * Runs a `.in(column, ids)` query in URL-safe batches and returns the merged
 * rows. `buildQuery` receives a chunk of ids and must return the fully-built
 * PostgREST query (a thenable) for that chunk. Throws if any batch errors, so
 * callers keep their existing try/catch behavior.
 */
export async function fetchInChunks<Row>(
  ids: string[],
  buildQuery: (
    chunk: string[],
  ) => PromiseLike<{ data: Row[] | null; error: unknown }>,
  size: number = SUPABASE_IN_CHUNK_SIZE,
): Promise<Row[]> {
  if (ids.length === 0) return [];
  const chunks = chunkArray(ids, size);
  const results = await Promise.all(chunks.map((chunk) => buildQuery(chunk)));
  const rows: Row[] = [];
  for (const { data, error } of results) {
    if (error) throw error;
    if (data) rows.push(...data);
  }
  return rows;
}
